import { CartItemType, MarketplaceThemeType } from '../../utils/sharedTypes'
import { formatBudgetRangeLabel, organismEnvelope, organismKey } from './marketplaceAssets'

// Mise en rayon du catalogue : filtres par financeur, mise en avant, découpage en sections. Partagé
// par la marketplace d'un groupe existant (`Marketplace.tsx`) et par celle à froid
// (`MarketplaceColdStart.tsx`), qui ne diffèrent que par la provenance du payload.

export const ALL_FUNDERS_FILTER = 'ALL'

// Le catalogue santé compte une soixantaine de thèmes : on n'en déroule qu'une partie par section,
// sinon la page devient un mur de cards impossible à parcourir.
export const SECTION_PREVIEW_COUNT = 6
const FEATURED_COUNT = 3

export const toCartItem = (theme: MarketplaceThemeType): CartItemType | null => {
  const product = theme.default_product
  if (!product) return null
  return {
    themeCode: theme.theme_code,
    themeTitle: theme.title,
    productCode: product.product_code,
    productName: product.name,
    listingPrice: product.listing_price ?? null,
    durationHours: product.duration ?? null,
    fundingCodes: theme.eligible_funding_organisms.map((organism) => organism.code || organism.name),
  }
}

/** Nombre d'avis, seul indicateur de popularité réellement disponible côté produit. */
const reviewCount = (theme: MarketplaceThemeType): number => theme.default_product?.ratings_count ?? 0

// `selling_point` est renseigné sur la quasi-totalité du catalogue : il ne discrimine rien. La mise
// en avant s'appuie donc sur les avis, une donnée qui, elle, varie réellement d'un thème à l'autre.
export const buildFeaturedThemes = (themes: MarketplaceThemeType[]): MarketplaceThemeType[] =>
  [...themes]
    .filter((theme) => reviewCount(theme) > 0 && (theme.default_product?.ratings_avg ?? 0) >= 4)
    .sort((a, b) => reviewCount(b) - reviewCount(a))
    .slice(0, FEATURED_COUNT)

export type FunderFilterType = { key: string; label: string }

export const buildFunderFilters = (themes: MarketplaceThemeType[]): FunderFilterType[] => {
  const labels = new Map<string, string>()
  themes.forEach((theme) => {
    theme.eligible_funding_organisms.forEach((organism) => {
      labels.set(organismKey(organism), organism.code || organism.name)
    })
  })
  return [...labels.entries()].map(([key, label]) => ({ key, label }))
}

export const filterThemesByFunder = (themes: MarketplaceThemeType[], funderKey: string): MarketplaceThemeType[] => {
  if (funderKey === ALL_FUNDERS_FILTER) return themes
  return themes.filter((theme) => theme.eligible_funding_organisms.some((organism) => organismKey(organism) === funderKey))
}

export type SectionType = {
  key: string
  /** Code du financeur porteur de l'enveloppe, `null` pour la section fourre-tout. */
  funderLabel: string | null
  budgetLabel: string | null
  themes: MarketplaceThemeType[]
}

/** Sections adossées aux financeurs dont on connaît l'enveloppe théorique : ce sont les seuls sur
 * lesquels on peut tenir un discours de budget. Chaque thème n'apparaît que dans une section. */
export const buildSections = (themes: MarketplaceThemeType[]): SectionType[] => {
  type FunderEnvelopeType = {
    label: string
    min: number
    max: number
    unit: 'eur' | 'hours' | undefined
    count: number
  }
  const fundersWithEnvelope = new Map<string, FunderEnvelopeType>()
  themes.forEach((theme) => {
    theme.eligible_funding_organisms.forEach((organism) => {
      const envelope = organismEnvelope(organism)
      if (!envelope) return
      const key = organismKey(organism)
      const existing = fundersWithEnvelope.get(key)
      if (existing) {
        existing.count += 1
        existing.min = Math.min(existing.min, envelope.min)
        existing.max = Math.max(existing.max, envelope.max)
      } else {
        fundersWithEnvelope.set(key, {
          label: organism.code || organism.name,
          min: envelope.min,
          max: envelope.max,
          unit: envelope.unit,
          count: 1,
        })
      }
    })
  })

  const assigned = new Set<string>()
  const sections: SectionType[] = []

  ;[...fundersWithEnvelope.entries()]
    .sort((a, b) => b[1].count - a[1].count)
    .forEach(([key, meta]) => {
      const sectionThemes = themes.filter(
        (theme) =>
          !assigned.has(theme.theme_code) &&
          theme.eligible_funding_organisms.some((organism) => organismKey(organism) === key),
      )
      sectionThemes.forEach((theme) => assigned.add(theme.theme_code))
      if (sectionThemes.length > 0) {
        sections.push({
          key,
          funderLabel: meta.label,
          budgetLabel: formatBudgetRangeLabel(meta.min, meta.max, meta.unit),
          themes: sectionThemes,
        })
      }
    })

  const remaining = themes.filter((theme) => !assigned.has(theme.theme_code))
  if (remaining.length > 0) {
    sections.push({ key: 'OTHER', funderLabel: null, budgetLabel: null, themes: remaining })
  }

  return sections
}
